import { Preferences } from '@capacitor/preferences';

/**
 * Utility to check if debug mode is enabled
 * Debug mode can be enabled in three ways:
 * 1. Through environment variables (isDebugMode)
 * 2. Through Capacitor Preferences (isDebug key)
 * 3. Explicitly passed in as a parameter
 */

// Cache the debug status to avoid frequent preference checks
let cachedDebugStatus: boolean | null = null;

/**
 * Check if debug mode is enabled
 * @param forceCheck Force a fresh check from preferences (bypassing cache)
 * @returns Promise resolving to boolean indicating if debug mode is enabled
 */
export const checkDebugMode = async (forceCheck = false): Promise<boolean> => {
  // If we have a cached value and aren't forcing a check, return it
  if (cachedDebugStatus !== null && !forceCheck) {
    return cachedDebugStatus;
  }

  try {
    // First check if we're in development mode
    const isDevelopment = process.env.NODE_ENV === 'development';
    if (isDevelopment) {
      cachedDebugStatus = true;
      return true;
    }

    // Then check preferences
    const { value } = await Preferences.get({ key: 'isDebug' });
    const isDebug = value === 'true';
    
    // Cache the result
    cachedDebugStatus = isDebug;
    return isDebug;
  } catch (error) {
    console.error('Error checking debug mode:', error);
    return false;
  }
};

/**
 * Set debug mode status in preferences
 * @param enabled Whether debug mode should be enabled
 */
const setDebugMode = async (enabled: boolean): Promise<void> => {
  try {
    await Preferences.set({
      key: 'isDebug',
      value: enabled.toString(),
    });
    
    // Update cache
    cachedDebugStatus = enabled;
  } catch (error) {
    console.error('Error setting debug mode:', error);
  }
};

/**
 * Toggle debug mode status
 * @returns Promise resolving to the new debug mode status
 */
const toggleDebugMode = async (): Promise<boolean> => {
  const currentStatus = await checkDebugMode(true);
  const newStatus = !currentStatus;
  await setDebugMode(newStatus);
  return newStatus;
};

/**
 * Synchronous check for debug mode - less accurate but doesn't require async/await
 * This uses the cached value if available, or falls back to checking if we're in development mode
 */
const isDebugModeSync = (): boolean => {
  // If we have a cached value, use it
  if (cachedDebugStatus !== null) {
    return cachedDebugStatus;
  }
  
  // Otherwise, check if we're in development mode
  return process.env.NODE_ENV === 'development';
};
